import { useEffect, useRef, useState } from 'react';
import { Shield } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import {
  archiveAdminUser,
  fetchAdminUserAssessments,
  grantAdminReports,
  type AdminAssessmentRow,
  type AdminUserRow,
} from '../../lib/apiClient';
import { ConfirmAction } from './ConfirmAction';
import { AssessmentHistory } from './AssessmentHistory';

/**
 * **Users** — the console's directory (spec §7).
 *
 * **Every active row is rendered.** There is no pagination, no search box, no
 * sort control, and no filter, because production has none of them: finding a
 * customer is the browser's `Ctrl+F`, and the specification records that this
 * *is* the workflow rather than an omission to be corrected (spec §12). Adding
 * any of the four would be a redesign, and would change how an operator works.
 *
 * The columns, their order, their alignment, and every fallback below are
 * production's. So is the pattern line's 160px truncation, which the
 * specification lists as an undecided cosmetic defect — and the default for an
 * undecided one is to reproduce it.
 *
 * **Clicking a row opens it** (spec §9). The chevron turns from `›` to `⌄` and
 * the row's assessment history is fetched and listed beneath it. It is a
 * **single-open accordion** — one id is held, so opening one closes another —
 * and the history is fetched when a row is first opened rather than with the
 * page, which is how production loads it.
 *
 * **The actions column carries Unlock Reports and Archive**, each a two-step
 * inline confirmation, and each stopping propagation so acting on a row never
 * opens or closes it. Production's column is `w-28`.
 */
type HistoryState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ok'; assessments: AdminAssessmentRow[] };

export function UsersTable({
  users,
  onChanged,
}: {
  users: AdminUserRow[];
  /** Called after an action lands, so the console reloads. */
  onChanged: () => void;
}) {
  const paying = users.filter((user) => user.isPayingCustomer).length;
  const revenueCents = users.reduce((total, user) => total + user.revenueCents, 0);

  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;

  // One id, which is what makes this an accordion: opening a row closes the one
  // that was open, exactly as production behaves.
  const [expanded, setExpanded] = useState<string | null>(null);
  const [histories, setHistories] = useState<Record<string, HistoryState>>({});
  /**
   * The customer whose reports were most recently granted. One id, as
   * production holds it: the confirmation line moves rather than accumulating.
   */
  const [granted, setGranted] = useState<string | null>(null);

  /**
   * Which rows have been read, in a ref rather than derived from `histories`.
   *
   * The effect must not depend on the state it writes: writing the loading
   * state would re-run it, and the re-run's cleanup would cancel the read that
   * was still in flight — an expanded row that never leaves "loading".
   */
  const requested = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!token || !expanded) return;
    // Already read. Re-opening a row shows what it showed before rather than
    // flashing a loading line at an operator who is comparing two customers.
    if (requested.current.has(expanded)) return;
    requested.current.add(expanded);

    let cancelled = false;
    const userId = expanded;
    setHistories((current) => ({ ...current, [userId]: { phase: 'loading' } }));

    void (async () => {
      const res = await fetchAdminUserAssessments(token, userId);
      if (cancelled) return;
      setHistories((current) => ({
        ...current,
        [userId]:
          res.state === 'ok' ? { phase: 'ok', assessments: res.assessments } : { phase: 'error' },
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [token, expanded]);

  /**
   * Drop a row's cached history and reload the console. A grant changes every
   * badge in the row, so forgetting the cache is what makes the next open read
   * again rather than show what was true before the action.
   */
  function forget(userId: string) {
    requested.current.delete(userId);
    setHistories((current) => {
      const next = { ...current };
      delete next[userId];
      return next;
    });
    onChanged();
  }

  return (
    <section className="flex flex-col gap-4" data-testid="admin-users">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold">Users</h2>
          <p className="text-sm text-muted-foreground" data-testid="users-summary">
            {`${users.length} active users. ${paying} paying.`}
          </p>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="users-revenue">
          {`${money(revenueCents)} total revenue`}
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border text-sm font-medium text-muted-foreground">
            <tr>
              {/* The chevron column. Production gives it no header text. */}
              <th scope="col" className="w-8 px-4 py-3" />
              <th scope="col" className="px-4 py-3">
                Name
              </th>
              <th scope="col" className="px-4 py-3">
                Email
              </th>
              <th scope="col" className="px-4 py-3 text-right">
                Revenue
              </th>
              <th scope="col" className="px-4 py-3">
                Last Login
              </th>
              <th scope="col" className="px-4 py-3">
                Joined
              </th>
              <th scope="col" className="w-16 px-4 py-3 text-center">
                Support
              </th>
              {/* The actions column. Production gives it no header text. */}
              <th scope="col" className="w-28 px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => (
              <Row
                key={user.id}
                user={user}
                expanded={expanded === user.id}
                history={histories[user.id]}
                token={token}
                granted={granted === user.id}
                onToggle={() => {
                  setExpanded((current) => (current === user.id ? null : user.id));
                }}
                onChanged={() => forget(user.id)}
                onGranted={() => {
                  setGranted(user.id);
                  forget(user.id);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Row({
  user,
  expanded,
  history,
  token,
  granted,
  onToggle,
  onChanged,
  onGranted,
}: {
  user: AdminUserRow;
  expanded: boolean;
  history: HistoryState | undefined;
  token: string | null;
  /** This row is the one that was most recently granted reports. */
  granted: boolean;
  onToggle: () => void;
  onChanged: () => void;
  onGranted: () => void;
}) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

  return (
    <>
    <tr
      data-testid="user-row"
      data-user-id={user.id}
      data-expanded={expanded ? 'true' : 'false'}
      onClick={onToggle}
      /*
        Production opens the row on a click anywhere in it. The keyboard
        equivalent is added here because a row that only a mouse can open is
        unusable without one; it offers no capability the click does not, and
        changes nothing an operator sees.
      */
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      className="cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <td className="w-8 px-4 py-3 text-muted-foreground" data-testid="user-chevron">
        {expanded ? '⌄' : '›'}
      </td>
      <td className="px-4 py-3">
        {name ? (
          <span className="font-medium">{name}</span>
        ) : (
          <span className="italic text-muted-foreground">no name</span>
        )}
        {/* The pattern line. Truncated at 160px, exactly as production does it. */}
        {user.latestType ? (
          <span
            className="block max-w-[160px] truncate text-xs text-muted-foreground"
            data-testid="user-pattern"
          >
            {user.latestType}
          </span>
        ) : null}
      </td>

      <td className="px-4 py-3" data-testid="user-email">
        {user.email ?? <span className="italic text-muted-foreground">no email</span>}
      </td>

      <td className="px-4 py-3 text-right" data-testid="user-revenue">
        <span
          className={
            user.revenueCents > 0 ? 'tabular-nums text-green-500' : 'tabular-nums text-muted-foreground'
          }
        >
          {money(user.revenueCents)}
        </span>
        {user.discountUsed ? (
          <span className="block text-xs text-yellow-500" data-testid="user-coupon">
            coupon used
          </span>
        ) : null}
      </td>

      <td className="px-4 py-3" data-testid="user-last-login">
        {user.lastLoginAt ? (
          localeDate(user.lastLoginAt)
        ) : (
          <span className="italic text-muted-foreground">Never</span>
        )}
      </td>

      <td className="px-4 py-3" data-testid="user-joined">
        {user.createdAt ? localeDate(user.createdAt) : null}
      </td>

      <td className="w-16 px-4 py-3 text-center">
        {/*
          An indicator, not a control: this is the customer's own grant of
          support access, and the console only reads it. It is not the Unlock
          Reports action, which is an operator write and belongs to §8.
        */}
        {user.supportAccessGrantedAt ? (
          <Shield
            className="mx-auto h-4 w-4 text-muted-foreground"
            aria-label="Support access granted"
            data-testid="user-support"
          />
        ) : null}
      </td>

      <td className="w-28 px-4 py-3">
        <span className="flex items-center gap-3">
          {/*
            Production replaces the Unlock control with this line once a grant
            lands, and holds it against a single customer id — so granting a
            second customer moves the line rather than lighting two.
          */}
          {granted ? (
            <span className="text-xs font-medium text-green-500" data-testid="reports-granted">
              Reports granted
            </span>
          ) : (
            <ConfirmAction
              testId="action-unlock"
              label="Unlock Reports"
              pendingLabel="Granting..."
              onConfirm={async () =>
                token !== null && (await grantAdminReports(token, user.id)) !== null
              }
              onChanged={onGranted}
            />
          )}
          <ConfirmAction
            testId="action-archive"
            label="Archive"
            pendingLabel="Archiving..."
            onConfirm={async () => token !== null && (await archiveAdminUser(token, user.id))}
            onChanged={onChanged}
          />
        </span>
      </td>
    </tr>

      {expanded ? (
        <tr data-testid="user-expansion" data-user-id={user.id}>
          <td colSpan={8} className="bg-background/40 px-4 py-4">
            {history?.phase === 'ok' && history.assessments.length > 0 ? (
              <AssessmentHistory
                assessments={history.assessments}
                token={token}
                onChanged={onChanged}
              />
            ) : null}

            {/* Production's own sentence for a customer who has taken nothing. */}
            {history?.phase === 'ok' && history.assessments.length === 0 ? (
              <p
                className="text-xs italic text-muted-foreground"
                data-testid="assessment-history-empty"
              >
                No assessments on record.
              </p>
            ) : null}

            {history?.phase === 'error' ? (
              <p className="text-sm font-medium" data-testid="assessment-history-error" role="status">
                Error loading admin data
              </p>
            ) : null}

            {history === undefined || history.phase === 'loading' ? (
              <p
                className="text-xs text-muted-foreground"
                data-testid="assessment-history-loading"
                role="status"
              >
                Loading assessment history...
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** `$X.XX`. Cents are the transport unit; production divides by 100 to display. */
function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Production renders these with the browser's locale date, so V2 does too — the
 * same call, and therefore the same string for the same operator.
 */
function localeDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}
